import React, { useEffect, useMemo, useState } from "react";
import { api, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { Calendar, Home, Video, Store, CheckCircle2, ClipboardList } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const STATUS_COLORS = {
  requested: "bg-amber-100 text-amber-900",
  confirmed: "bg-emerald-100 text-emerald-900",
  rescheduled: "bg-blue-100 text-blue-900",
  completed: "bg-slate-200 text-slate-800",
  cancelled: "bg-red-100 text-red-900",
  no_show: "bg-red-100 text-red-900",
};
const TYPE_ICON = { shop_visit: Store, home_visit: Home, video_call: Video };

export default function SellerAppointments() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);

  const typeById = useMemo(() => Object.fromEntries(types.map((t) => [t.id, t])), [types]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [a, t] = await Promise.all([
        api.get("/seller/appointments"),
        api.get("/clothing-types"),
      ]);
      setRows(a.data || []);
      setTypes(t.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="seller-appointments-page">
      <div className="mb-6">
        <div className="overline mb-2">Stitching</div>
        <h1 className="font-display text-4xl">Measurement Appointments</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Appointments assigned to your studio. Confirm slots, then complete with saved measurements for the customer.</p>
      </div>

      {loading ? (
        <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="seller-appointments-loading">Loading…</div>
      ) : error ? (
        <div className="p-8 text-center text-red-700" data-testid="seller-appointments-error">{error}</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[hsl(var(--kb-border))] py-14 text-center" data-testid="seller-appointments-empty">
          <Calendar size={28} className="mx-auto text-[hsl(var(--kb-muted))] mb-3" />
          <div className="font-display text-xl">No appointments assigned yet</div>
          <div className="text-sm text-[hsl(var(--kb-muted))]">Admin will assign customer appointments to your shop here.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((a) => {
            const Icon = TYPE_ICON[a.appointment_type] || Calendar;
            return (
              <div key={a.id} className="bg-white border border-[hsl(var(--kb-border))] p-4 flex gap-4 flex-wrap" data-testid={`seller-appt-${a.id}`}>
                <div className="p-2 bg-[hsl(var(--kb-surface-alt))] self-start">
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg">{typeById[a.clothing_type_id]?.name || "–"}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_COLORS[a.status]}`} data-testid={`seller-appt-status-${a.id}`}>
                      {a.status.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                    {a.confirmed_date || a.preferred_date} · {a.confirmed_time_slot || a.preferred_time_slot} · Contact {a.contact_number}
                  </div>
                  {a.notes && <div className="text-xs italic text-[hsl(var(--kb-muted))] mt-2">"{a.notes}"</div>}
                  {Array.isArray(a.reference_images) && a.reference_images.length > 0 && (
                    <div className="mt-3 flex gap-2 flex-wrap" data-testid={`seller-appt-ref-${a.id}`}>
                      {a.reference_images.map((url, idx) => (
                        <a key={idx} href={imgUrl(url)} target="_blank" rel="noopener noreferrer" className="block w-14 h-14 border border-[hsl(var(--kb-border))] overflow-hidden">
                          <img src={imgUrl(url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 items-start">
                  {(a.status === "requested" || a.status === "rescheduled") && (
                    <button onClick={() => setConfirmTarget(a)} className="kb-btn-outline !py-1.5 !px-3 !text-xs inline-flex items-center gap-1" data-testid={`seller-confirm-${a.id}`}>
                      <CheckCircle2 size={12} /> Confirm
                    </button>
                  )}
                  {a.status === "confirmed" && (
                    <button onClick={() => setCompleteTarget(a)} className="kb-btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1" data-testid={`seller-complete-${a.id}`}>
                      <ClipboardList size={12} /> Complete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SellerConfirmDialog target={confirmTarget} onClose={() => setConfirmTarget(null)} onSaved={() => { setConfirmTarget(null); load(); }} />
      <SellerCompleteDialog target={completeTarget} onClose={() => setCompleteTarget(null)} onSaved={() => { setCompleteTarget(null); load(); }} />
    </div>
  );
}

function SellerConfirmDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({ confirmed_date: "", confirmed_time_slot: "", assigned_staff_name: "", service_charge: "", note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    setForm({
      confirmed_date: target.confirmed_date || target.preferred_date || "",
      confirmed_time_slot: target.confirmed_time_slot || target.preferred_time_slot || "",
      assigned_staff_name: target.assigned_staff_name || "",
      service_charge: target.service_charge || "",
      note: "",
    });
  }, [target]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/seller/appointments/${target.id}/confirm`, {
        confirmed_date: form.confirmed_date,
        confirmed_time_slot: form.confirmed_time_slot,
        assigned_staff_name: form.assigned_staff_name || null,
        service_charge: form.service_charge === "" ? null : Number(form.service_charge),
        note: form.note || null,
      });
      toast.success("Confirmed");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="seller-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Confirm appointment</DialogTitle>
          <DialogDescription>Lock the slot and optionally assign a tailor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input type="date" className="kb-input" value={form.confirmed_date} onChange={(e) => setForm({ ...form, confirmed_date: e.target.value })} />
          <input className="kb-input" placeholder="Time slot" value={form.confirmed_time_slot} onChange={(e) => setForm({ ...form, confirmed_time_slot: e.target.value })} />
          <input className="kb-input" placeholder="Assigned tailor / staff" value={form.assigned_staff_name} onChange={(e) => setForm({ ...form, assigned_staff_name: e.target.value })} />
          <input type="number" className="kb-input" placeholder="Service charge (₹)" value={form.service_charge} onChange={(e) => setForm({ ...form, service_charge: e.target.value })} />
          <textarea rows={2} className="kb-input" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>
        <DialogFooter>
          <button className="kb-btn-outline" onClick={onClose} type="button">Cancel</button>
          <button className="kb-btn-primary" onClick={save} disabled={saving} data-testid="seller-confirm-save-btn" type="button">
            {saving ? "Saving…" : "Confirm"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SellerCompleteDialog({ target, onClose, onSaved }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [form, setForm] = useState({ profile_name: "", unit: "inches", notes: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) return;
    setForm({ profile_name: `Measured at appointment ${new Date().toLocaleDateString("en-IN")}`, unit: "inches", notes: "" });
    setValues({});
    setLoading(true);
    (async () => {
      try {
        // Resolve clothing type slug via list
        const t = await api.get("/admin/stitching/clothing-types");
        const ct = (t.data || []).find((x) => x.id === target.clothing_type_id);
        if (ct) {
          const r = await api.get(`/admin/stitching/clothing-types/${ct.id}/fields`);
          setFields((r.data || []).filter((f) => f.is_active));
        } else {
          setFields([]);
        }
      } catch {
        setFields([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [target]);

  const save = async () => {
    if (!form.profile_name.trim()) return toast.error("Profile name required");
    const payload = {
      profile_name: form.profile_name.trim(),
      unit: form.unit,
      notes: form.notes.trim() || null,
      values: fields
        .filter((f) => values[f.key] !== undefined && values[f.key] !== "")
        .map((f) => ({ key: f.key, value: values[f.key], unit: form.unit })),
    };
    setSaving(true);
    try {
      await api.post(`/seller/appointments/${target.id}/complete`, payload);
      toast.success("Completed — measurements saved for customer");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="seller-complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Complete appointment</DialogTitle>
          <DialogDescription>Record the final measurements — they'll be saved to the customer's profile automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <input className="kb-input" placeholder="Profile name" value={form.profile_name} onChange={(e) => setForm({ ...form, profile_name: e.target.value })} data-testid="complete-profile-name" />
          <select className="kb-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
            <option value="inches">Inches</option>
            <option value="cm">Centimeters</option>
          </select>
          {loading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.id}>
                  <label className="text-xs flex items-center gap-1 mb-1 text-[hsl(var(--kb-muted))]">
                    {f.label}{f.is_required && <span className="text-red-600">•</span>}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min={f.min_value ?? undefined}
                    max={f.max_value ?? undefined}
                    className="kb-input"
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                    data-testid={`complete-field-${f.key}`}
                  />
                </div>
              ))}
            </div>
          )}
          <textarea rows={2} className="kb-input" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <DialogFooter>
          <button className="kb-btn-outline" onClick={onClose} type="button">Cancel</button>
          <button className="kb-btn-primary" onClick={save} disabled={saving} data-testid="complete-save-btn" type="button">
            {saving ? "Saving…" : "Complete & Save Measurements"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
